/**
 * repurpose/FromPrompt.jsx
 * A sentence in; a video, its captions and a draft out.
 *
 * The chain runs on the server, in a row that outlives this screen — so this
 * component is a window onto it rather than the thing driving it. Closing the
 * tab, reloading, or coming back tomorrow all show the same chain at whatever
 * stage it has reached, because the five-minute sweep keeps moving it along
 * either way. That is the whole reason it is built this way; see
 * services/pipeline.js.
 *
 * What it must be honest about: this spends money twice, minutes apart, and it
 * ends at a draft. It does not post. The last step is a person opening Drafts
 * and approving it, exactly as with everything else in the app.
 *
 * Dependencies: services/api.js, components/Icons.jsx
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../services/api.js';
import { Check, Film, Sparkle, Warning } from '../Icons.jsx';

const money = (pence) => `£${(pence / 100).toFixed(2)}`;

/** How long between asking the server how a chain is getting on. */
const POLL_MS = 5000;

/** The stages in order, for the progress trail. */
const STEPS = [
  { key: 'filming', label: 'Video' },
  { key: 'writing', label: 'Captions' },
  { key: 'drafted', label: 'Draft' },
];

/**
 * Which step a chain is ON, counting from 1. Past the last step means done.
 *
 * One-based on purpose: "the step it is on" is the thing both the tick and the
 * highlight are asking about, and counting the steps it has finished instead
 * puts the highlight one place ahead the whole way down.
 */
const ON_STEP = { queued: 1, filming: 1, writing: 2, drafted: STEPS.length + 1 };

/**
 * A stopped chain is read from what it actually has rather than from its
 * stage, because "Write the captions again" beside a Video step marked
 * never-done is a contradiction — and the video is the half that was paid
 * for, so it is the half worth showing as finished.
 */
function stepOf(chain) {
  if (!chain) return 1;
  const known = ON_STEP[chain.stage];
  if (known !== undefined) return known;
  if (chain.postId) return STEPS.length + 1;
  if (chain.repurposeJobId) return 3;
  return chain.mediaId ? 2 : 1;
}

/**
 * The one shape this run will film in, in words.
 *
 * A chain makes ONE video however many places it is going. Labelling each
 * channel with the shape it would have picked on its own reads as a video per
 * channel — it is not, and it would cost twice as much if it were. So the
 * shape is worked out for the whole run and said once.
 *
 * The rule itself comes from the server (aspect.priority), so this cannot
 * drift away from what the chain will actually do.
 */
function shapeOf(chosen, rule) {
  if (!rule || !chosen.length) return null;

  const winner = rule.priority.find((key) => chosen.includes(key));
  const aspect = rule.forChannel[winner];
  if (!aspect) return null;

  const others = chosen.filter((key) => key !== winner && rule.forChannel[key] !== aspect);

  return {
    aspect,
    word: rule.describe?.[aspect] ?? aspect,
    // Only explain when something had to give. With one channel chosen there
    // is no compromise to justify, and saying so anyway is noise. The words
    // come from the server, beside the rule that produced them.
    why: others.length ? (rule.because?.[aspect] ?? '') : '',
  };
}

/** How long a finished chain stays the thing this screen opens on. */
const STILL_INTERESTING_HOURS = 24;

/**
 * Whether a chain is what somebody came back for.
 *
 * Anything still going, plus anything that finished within the last day —
 * long enough to cover starting one in the evening and looking in the
 * morning, short enough that last week's does not greet you.
 */
function worthShowing(chain) {
  if (chain.running) return true;
  if (!chain.completedAt) return false;
  const hours = (Date.now() - new Date(chain.completedAt).getTime()) / 3_600_000;
  return hours >= 0 && hours < STILL_INTERESTING_HOURS;
}

export default function FromPrompt({ brandId, onDrafted }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [prompt, setPrompt] = useState('');
  const [goal, setGoal] = useState('');
  const [channels, setChannels] = useState([]);
  const [seconds, setSeconds] = useState(5);
  const [resolution, setResolution] = useState('720p');
  const [draftMode, setDraftMode] = useState(false);

  const [starting, setStarting] = useState(false);
  const [chain, setChain] = useState(null);
  const [busy, setBusy] = useState(false);

  // Held in a ref as well so the poll can see the current chain without being
  // torn down and restarted every time the chain changes.
  const chainRef = useRef(null);
  chainRef.current = chain;

  const load = useCallback(async () => {
    if (!brandId) return;
    setLoading(true);
    try {
      const res = await api.pipeline.options(brandId);
      setData(res);
      setChannels((c) => (c.length ? c : res.channels.filter((x) => x.available).map((x) => x.key)));
      setSeconds((s) => s || res.limits.duration.default);
      setResolution((r) => r || res.limits.resolutions.default);

      // Pick up where the last visit left off.
      //
      // Not only the running ones. The point of this whole arrangement is that
      // you can start a chain, close the tab and come back to a finished
      // draft — and coming back to an empty form with the result filed under
      // "Earlier" is exactly the thing that makes people think it was lost.
      // Anything still going, or anything that finished recently enough to
      // still be the reason you opened this screen.
      setChain((current) => current ?? res.chains.find(worthShowing) ?? null);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [brandId]);

  useEffect(() => {
    load();
  }, [load]);

  // A different brand is a different set of connections and a different
  // library. Nothing carries across.
  useEffect(() => {
    setChain(null);
    setPrompt('');
    setGoal('');
    setChannels([]);
  }, [brandId]);

  /**
   * While a chain is running, ask how it is doing.
   *
   * The asking is what carries it forward with a tab open — GET /pipeline/:id
   * advances it server-side. When no tab is open the cron does the same, so
   * this is a convenience, not the mechanism.
   */
  useEffect(() => {
    if (!chain?.running) return undefined;

    const tick = async () => {
      const current = chainRef.current;
      if (!current?.running) return;
      try {
        const res = await api.pipeline.get(current.id);
        setChain(res.chain);
        if (res.chain.stage === 'drafted') onDrafted?.(res.chain);
      } catch {
        // A hiccup asking is not a failed chain. The next tick will ask again,
        // and the cron is moving it along regardless.
      }
    };

    const timer = setInterval(tick, POLL_MS);
    return () => clearInterval(timer);
  }, [chain?.running, chain?.id, onDrafted]);

  if (loading && !data) return <p className="muted">Loading…</p>;

  const limits = data?.limits ?? { minPromptChars: 80, maxPromptChars: 5000 };
  const available = (data?.channels ?? []).filter((c) => c.available);
  const tooShort = prompt.trim().length < limits.minPromptChars;
  const ready = !tooShort && channels.length > 0 && !starting && !chain?.running;

  // Priced from the server's own rate table, sent with the options, so the
  // button never promises a number the server would disagree with. The server
  // prices it again on start and is the one that decides.
  const rates = data?.pricing;
  const perSecond = rates?.perSecondPence?.[resolution]?.[draftMode ? 'draft' : 'full'] ?? 0;
  const estimate = seconds * perSecond + (rates?.captionsPence ?? 0);

  const shape = shapeOf(channels, data?.aspect);

  function toggle(key) {
    setChannels((c) => (c.includes(key) ? c.filter((k) => k !== key) : [...c, key]));
  }

  async function start() {
    setStarting(true);
    setError(null);
    try {
      const res = await api.pipeline.start({
        brandId,
        prompt: prompt.trim(),
        goal,
        channels,
        duration: seconds,
        resolution,
        draft: draftMode,
      });
      setChain(res.chain);
    } catch (e) {
      setError(e);
    } finally {
      setStarting(false);
    }
  }

  async function act(fn) {
    setBusy(true);
    setError(null);
    try {
      const res = await fn();
      setChain(res.chain);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }

  const onStep = stepOf(chain);

  return (
    <div className="rp-layout">
      <section className="rp-source card">
        <header className="rp-source__head">
          <h2>Describe the video</h2>
        </header>

        <div className="rp-field">
          <label htmlFor="pp-prompt">What happens in it?</label>
          <textarea
            id="pp-prompt"
            rows={6}
            value={prompt}
            maxLength={limits.maxPromptChars}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="A slow pan across a sunlit workshop bench covered in hand tools, sawdust in the air, warm morning light — for people who make things by hand."
          />
          <div className={`rp-count${tooShort ? ' rp-count--short' : ''}`}>
            {prompt.trim().length} / {limits.minPromptChars} minimum
          </div>
          {/* The one thing about this screen that surprises people: the words
              are used twice. Said here rather than discovered later. */}
          <p className="muted">
            These words are filmed <em>and</em> written from — the captions come from this
            description, so say who it is for as well as what is in it.
          </p>
        </div>

        <div className="rp-field">
          <label htmlFor="pp-goal">What is it for? (optional)</label>
          <input
            id="pp-goal"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            placeholder="Bookings for the autumn workshops"
          />
        </div>

        <div className="pp-settings">
          <div className="rp-field">
            <label htmlFor="pp-seconds">Length</label>
            <input
              id="pp-seconds"
              type="number"
              min={limits.duration?.min ?? 1}
              max={limits.duration?.max ?? 20}
              value={seconds}
              onChange={(e) => setSeconds(Number(e.target.value))}
            />
          </div>

          <div className="rp-field">
            <label htmlFor="pp-res">Quality</label>
            <select id="pp-res" value={resolution} onChange={(e) => setResolution(e.target.value)}>
              {(limits.resolutions?.values ?? ['720p', '1080p']).map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <label className="pp-check">
            <input
              type="checkbox"
              checked={draftMode}
              onChange={(e) => setDraftMode(e.target.checked)}
            />
            <span>
              Draft mode
              <span className="muted"> — rougher, about a quarter of the price. Good for trying an idea.</span>
            </span>
          </label>
        </div>
      </section>

      <div className="rp-right">
        <section className="rp-channels card">
          <header className="rp-channels__head">
            <h2>Where it goes</h2>
          </header>

          <ul className="rp-channel-list">
            {(data?.channels ?? []).map((c) => {
              const on = channels.includes(c.key);
              return (
                <li key={c.key}>
                  <button
                    type="button"
                    className={`rp-channel ${on ? 'is-on' : ''}`}
                    onClick={() => toggle(c.key)}
                    disabled={!c.available || chain?.running}
                    aria-pressed={on}
                  >
                    <span className="rp-channel__tick">{on && <Check size={13} />}</span>
                    <span className="rp-channel__body">
                      <span className="rp-channel__name">{c.label}</span>
                      {/* How the platform carries it — a fixed fact about
                          Instagram and Facebook. The shape is NOT here: one
                          chain makes one video, and a shape per card would
                          promise one video per card. It is named once, below. */}
                      <span className="rp-channel__note">
                        {c.available ? c.as : c.reason}
                      </span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>

          {channels.length > 0 && shape && (
            <p className="pp-shape">
              <Film size={14} />
              <span>
                <strong>
                  One video, filmed {shape.aspect} ({shape.word}).
                </strong>{' '}
                {shape.why}
              </span>
            </p>
          )}

          {available.length === 0 && (
            <p className="rp-error">
              <Warning size={14} /> Nowhere to send this yet. Connect a Facebook or Instagram
              account to this brand on the Brands screen.
            </p>
          )}
        </section>

        <div className="rp-go card">
          <button
            type="button"
            className="btn btn--primary btn--wide"
            onClick={start}
            disabled={!ready}
          >
            <Film size={16} />
            {starting ? 'Starting…' : `Make it — about ${money(estimate)}`}
          </button>
          <p className="muted">
            Makes the video, writes a caption for each place, and saves one draft with the video
            attached. It does not post: you approve it in Drafts like anything else.
          </p>
          {data && (
            <p className="muted">
              {money(data.spend.monthPence)} of {money(data.spend.capPence)} used this month.
            </p>
          )}
        </div>
      </div>

      {error && (
        <p className="rp-error pp-wide">
          <Warning size={14} /> {error.message}
        </p>
      )}

      {chain && (
        <section className="pp-chain card pp-wide">
          <header className="pp-chain__head">
            <h2>{chain.label}</h2>
            {chain.running && <span className="pp-spinner" aria-hidden="true" />}
          </header>

          <ol className="pp-steps">
            {STEPS.map((step, i) => (
              <li
                key={step.key}
                className={onStep > i + 1 ? 'is-done' : onStep === i + 1 ? 'is-now' : ''}
              >
                <span className="pp-steps__dot" aria-hidden="true">
                  {onStep > i + 1 ? <Check size={12} /> : i + 1}
                </span>
                {step.label}
              </li>
            ))}
          </ol>

          <p className="muted pp-chain__prompt">{chain.prompt}</p>

          {chain.running && (
            <p className="muted">
              This keeps going whether or not you stay on this screen — it is checked every few
              minutes on the server. Come back whenever.
            </p>
          )}

          {chain.error && (
            <p className="rp-error">
              <Warning size={14} /> {chain.error}
            </p>
          )}

          <div className="pp-chain__actions">
            {chain.stage === 'drafted' && chain.postId && (
              // Into the Composer, which is where Drafts sends you too — the
              // video and both captions are there to read before approving.
              // A Link rather than an href: an <a> would reload the whole app.
              <Link className="btn btn--primary" to={`/create?post=${chain.postId}`}>
                <Check size={15} /> Open the draft
              </Link>
            )}

            {chain.stage === 'failed' && chain.mediaId && (
              <button
                type="button"
                className="btn"
                disabled={busy}
                onClick={() => act(() => api.pipeline.retry(chain.id))}
              >
                <Sparkle size={15} /> Write the captions again
              </button>
            )}

            {chain.running && (
              <button
                type="button"
                className="btn btn--danger"
                disabled={busy}
                onClick={() => act(() => api.pipeline.cancel(chain.id))}
              >
                Stop it
              </button>
            )}

            {!chain.running && (
              <button type="button" className="btn" onClick={() => setChain(null)}>
                Start another
              </button>
            )}
          </div>

          {chain.spentPence > 0 && (
            <p className="muted">So far this one has cost {money(chain.spentPence)}.</p>
          )}
        </section>
      )}

      {(data?.chains ?? []).filter((c) => !c.running && c.id !== chain?.id).length > 0 && (
        <section className="pp-past pp-wide">
          <h3>Earlier</h3>
          <ul>
            {data.chains
              .filter((c) => !c.running && c.id !== chain?.id)
              .slice(0, 6)
              .map((c) => (
                <li key={c.id}>
                  <button type="button" className="pp-past__item" onClick={() => setChain(c)}>
                    <span className={`pp-past__tag pp-past__tag--${c.stage}`}>{c.label}</span>
                    <span className="pp-past__prompt">{c.prompt}</span>
                  </button>
                </li>
              ))}
          </ul>
        </section>
      )}
    </div>
  );
}
